import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import QRScannerButton from "../components/QRScannerButton";
import { ArrowRightLeft, Undo2, Send, Router as RouterIcon } from "lucide-react";

export default function TechnicianStockPage({ selfOnly = false }) {
  const [techs, setTechs] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selected, setSelected] = useState("");
  const [stock, setStock] = useState([]);

  // Assign from main stock
  const [tid, setTid] = useState("");
  const [mid, setMid] = useState("");
  const [qty, setQty] = useState(1);
  const [msg, setMsg] = useState("");

  // Transfer between techs modal
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState({ from_technician_id: "", to_technician_id: "", material_id: "", quantity: 1 });
  const [tErr, setTErr] = useState("");

  // Return to main modal
  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState({ technician_id: "", material_id: "", material_name: "", available: 0, quantity: 1 });
  const [rErr, setRErr] = useState("");

  useEffect(() => {
    if (selfOnly) {
      api.get("/technician-stock").then((r) => setStock(r.data));
      api.get("/equipment-units?status=com_tecnico").then((r) => setMyEquip(r.data)).catch(() => {});
    } else {
      api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "tecnico")));
      api.get("/materials").then((r) => setMaterials(r.data));
      api.get("/equipment-units?status=estoque").then((r) => setEqUnits(r.data));
    }
  }, [selfOnly]);

  const [eqUnits, setEqUnits] = useState([]);
  const [myEquip, setMyEquip] = useState([]);
  const [eqSN, setEqSN] = useState("");
  const [eqTech, setEqTech] = useState("");
  const [eqMsg, setEqMsg] = useState("");
  // Equipment held by the selected technician (for the read-only view)
  const [techEquip, setTechEquip] = useState([]);
  const assignEquipment = async (e) => {
    e.preventDefault();
    setEqMsg("");
    const unit = eqUnits.find((u) => (u.serial_number || "").toLowerCase() === eqSN.trim().toLowerCase());
    if (!unit) { setEqMsg("SN não encontrado no estoque da empresa"); return; }
    try {
      await api.post(`/equipment-units/${unit.id}/assign`, { technician_id: eqTech });
      setEqMsg(`Equipamento ${unit.serial_number} atribuído com sucesso`);
      setEqSN("");
      api.get("/equipment-units?status=estoque").then((r) => setEqUnits(r.data));
    } catch (err) { setEqMsg(formatError(err)); }
  };

  const loadStock = (id) => {
    setSelected(id);
    if (!id) { setStock([]); setTechEquip([]); return; }
    api.get(`/technician-stock?technician_id=${id}`).then((r) => setStock(r.data));
    api.get(`/equipment-units?status=com_tecnico&technician_id=${id}`).then((r) => setTechEquip(r.data));
  };

  const refreshEquip = () => {
    if (selected) api.get(`/equipment-units?status=com_tecnico&technician_id=${selected}`).then((r) => setTechEquip(r.data));
  };

  const returnEquip = async (unit) => {
    if (!window.confirm(`Devolver equipamento SN ${unit.serial_number} ao estoque da empresa?`)) return;
    try {
      await api.post(`/equipment-units/${unit.id}/return`);
      refreshEquip();
      api.get("/equipment-units?status=estoque").then((r) => setEqUnits(r.data));
    } catch (e) { alert(formatError(e)); }
  };

  const assign = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await api.post("/technician-stock/transfer", { technician_id: tid, material_id: mid, quantity: Number(qty) });
      setMsg("Atribuído ao técnico com sucesso");
      if (selected === tid) loadStock(tid);
      setQty(1);
    } catch (err) { setMsg(formatError(err)); }
  };

  const openTransfer = (item) => {
    setTForm({
      from_technician_id: selected,
      to_technician_id: "",
      material_id: item.material_id,
      quantity: 1,
    });
    setTErr("");
    setTOpen(true);
  };
  const doTransfer = async () => {
    setTErr("");
    try {
      await api.post("/technician-stock/transfer-between", { ...tForm, quantity: Number(tForm.quantity) });
      setTOpen(false);
      loadStock(selected);
    } catch (e) { setTErr(formatError(e)); }
  };

  const openReturn = (item) => {
    setRForm({
      technician_id: selected,
      material_id: item.material_id,
      material_name: item.material_name,
      available: item.quantity,
      quantity: 1,
    });
    setRErr("");
    setROpen(true);
  };
  const doReturn = async () => {
    setRErr("");
    try {
      await api.post("/technician-stock/return", {
        technician_id: rForm.technician_id,
        material_id: rForm.material_id,
        quantity: Number(rForm.quantity),
      });
      setROpen(false);
      loadStock(selected);
    } catch (e) { setRErr(formatError(e)); }
  };

  if (selfOnly) {
    return (
      <div data-testid="my-stock-page">
        <PageHeader eyebrow="Estoque" title="Meu Estoque">
          Materiais e equipamentos sob sua responsabilidade
        </PageHeader>
        <div className="card mb-6">
          <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
            <h3 className="label-caps">Materiais</h3>
          </div>
          <table className="data">
            <thead><tr><th>Código</th><th>Material</th><th>Qtd Disponível</th></tr></thead>
            <tbody>
              {stock.map((s) => (
                <tr key={s.material_id}>
                  <td className="mono">{s.material_code}</td>
                  <td>{s.material_name}</td>
                  <td className="mono font-semibold">{s.quantity}</td>
                </tr>
              ))}
              {stock.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Você ainda não recebeu materiais.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="card" data-testid="my-equipment-section">
          <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <RouterIcon size={16} />
            <h3 className="label-caps">Meus Equipamentos</h3>
            <span className="ml-auto text-xs mono" style={{ color: "var(--text-mute)" }}>{myEquip.length} unidade(s)</span>
          </div>
          <table className="data">
            <thead><tr><th>Nº Série</th><th>Modelo</th></tr></thead>
            <tbody>
              {myEquip.map((u) => (
                <tr key={u.id} data-testid={`my-equip-${u.serial_number}`}>
                  <td className="mono font-semibold">{u.serial_number}</td>
                  <td>{u.model_name || "-"}</td>
                </tr>
              ))}
              {myEquip.length === 0 && <tr><td colSpan={2} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Você não possui equipamentos atribuídos.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const selectedTechName = techs.find((t) => t.id === selected)?.name;

  return (
    <div data-testid="tech-stock-page">
      <PageHeader eyebrow="Estoque" title="Estoque dos Técnicos">
        Atribua materiais a técnicos, transfira entre eles ou devolva ao estoque central.
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form className="card p-5" onSubmit={assign} data-testid="transfer-form">          <h3 className="label-caps mb-4">Atribuir Material (do estoque central)</h3>
          {msg && <div className="mb-3 text-xs p-2 border" style={{ borderColor: "var(--border-strong)", color: "var(--text-dim)" }}>{msg}</div>}
          <label className="label-caps block mb-1">Técnico</label>
          <select className="select mb-3" value={tid} onChange={(e) => setTid(e.target.value)} required data-testid="transfer-tech">
            <option value="">Selecione</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="label-caps block mb-1">Material</label>
          <select className="select mb-3" value={mid} onChange={(e) => setMid(e.target.value)} required data-testid="transfer-material">
            <option value="">Selecione</option>
            {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name} (Est: {m.stock})</option>)}
          </select>
          <label className="label-caps block mb-1">Quantidade</label>
          <input className="input mono mb-4" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} required data-testid="transfer-qty" />
          <button className="btn btn-primary w-full" type="submit" data-testid="transfer-submit">Atribuir</button>
        </form>

        <form className="card p-5" onSubmit={assignEquipment} data-testid="assign-eq-form">
          <h3 className="label-caps mb-4"><RouterIcon size={13} className="inline mr-1" /> Atribuir Equipamento (por SN)</h3>
          {eqMsg && <div className="mb-3 text-xs p-2 border" style={{ borderColor: "var(--border-strong)", color: "var(--text-dim)" }}>{eqMsg}</div>}
          <label className="label-caps block mb-1">Técnico</label>
          <select className="select mb-3" value={eqTech} onChange={(e) => setEqTech(e.target.value)} required data-testid="assign-eq-tech">
            <option value="">Selecione</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="label-caps block mb-1">Nº de Série</label>
          <div className="flex gap-2 mb-3">
            <input className="input mono flex-1" placeholder="Digite ou bipe o SN" value={eqSN} onChange={(e) => setEqSN(e.target.value)} required data-testid="assign-eq-sn" />
            <QRScannerButton onScan={(t) => setEqSN(t)} testid="assign-eq-qr" />
          </div>
          <div className="text-xs mb-4" style={{ color: "var(--text-mute)" }}>
            Disponíveis no estoque: <b className="mono" style={{ color: "var(--text)" }}>{eqUnits.length}</b>
          </div>
          <button className="btn btn-primary w-full" type="submit" data-testid="assign-eq-submit">Atribuir Equipamento</button>
        </form>

        <div className="card lg:col-span-3">
          <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
            <h3 className="label-caps">Estoque de:</h3>
            <select className="select w-64" value={selected} onChange={(e) => loadStock(e.target.value)} data-testid="view-tech-stock">
              <option value="">Selecione um técnico</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <table className="data">
            <thead><tr><th>Código</th><th>Material</th><th>Qtd</th><th>Ações</th></tr></thead>
            <tbody>
              {stock.map((s) => (
                <tr key={s.material_id} data-testid={`tech-stock-row-${s.material_code}`}>
                  <td className="mono">{s.material_code}</td>
                  <td>{s.material_name}</td>
                  <td className="mono">{s.quantity}</td>
                  <td>
                    <div className="flex gap-1">
                      <button className="btn btn-ghost" onClick={() => openTransfer(s)} title="Transferir para outro técnico" data-testid={`transfer-btn-${s.material_code}`}>
                        <ArrowRightLeft size={13} /> Transferir
                      </button>
                      <button className="btn btn-ghost" onClick={() => openReturn(s)} title="Devolver ao estoque central" data-testid={`return-btn-${s.material_code}`}>
                        <Undo2 size={13} /> Devolver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {stock.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>{selected ? "Sem itens." : "Selecione um técnico."}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="card lg:col-span-3">
          <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
            <RouterIcon size={16} />
            <h3 className="label-caps">Equipamentos com o técnico {selectedTechName ? `· ${selectedTechName}` : ""}</h3>
            <span className="ml-auto text-xs mono" style={{ color: "var(--text-mute)" }}>{techEquip.length} unidade(s)</span>
          </div>
          <table className="data">
            <thead><tr><th>Modelo</th><th>Nº de Série</th><th>Cliente</th><th>Desde</th><th>Ações</th></tr></thead>
            <tbody>
              {techEquip.map((u) => (
                <tr key={u.id} data-testid={`tech-eq-row-${u.serial_number}`}>
                  <td>{u.model_name}</td>
                  <td className="mono font-semibold">{u.serial_number}</td>
                  <td className="text-xs">{u.client_name || "-"}</td>
                  <td className="text-xs" style={{ color: "var(--text-mute)" }}>{u.assigned_at ? new Date(u.assigned_at).toLocaleDateString("pt-BR") : "-"}</td>
                  <td>
                    <button className="btn btn-ghost" onClick={() => returnEquip(u)} data-testid={`return-eq-${u.serial_number}`} title="Devolver ao estoque da empresa">
                      <Undo2 size={13} /> Devolver
                    </button>
                  </td>
                </tr>
              ))}
              {techEquip.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm" style={{ color: "var(--text-mute)" }}>{selected ? "Nenhum equipamento com este técnico." : "Selecione um técnico para ver os equipamentos."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={tOpen} onClose={() => setTOpen(false)} title="Transferir entre Técnicos" testid="transfer-between-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setTOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doTransfer} data-testid="confirm-transfer-between">Transferir</button>
        </>}>
        {tErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{tErr}</div>}
        <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
          <div className="label-caps mb-1">De</div>
          <div className="text-sm">{selectedTechName}</div>
        </div>
        <label className="label-caps block mb-1">Para o Técnico</label>
        <select className="select mb-3" value={tForm.to_technician_id} onChange={(e) => setTForm({ ...tForm, to_technician_id: e.target.value })} data-testid="transfer-to-tech">
          <option value="">Selecione</option>
          {techs.filter((t) => t.id !== selected).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <label className="label-caps block mb-1">Quantidade</label>
        <input className="input mono" type="number" min="1" value={tForm.quantity} onChange={(e) => setTForm({ ...tForm, quantity: e.target.value })} data-testid="transfer-between-qty" />
      </Modal>

      <Modal open={rOpen} onClose={() => setROpen(false)} title="Devolver ao Estoque Central" testid="return-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setROpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doReturn} data-testid="confirm-return">Devolver</button>
        </>}>
        {rErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{rErr}</div>}
        <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
          <div className="label-caps mb-1">Material</div>
          <div className="text-sm">{rForm.material_name}</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>Disponível com o técnico: <b className="mono" style={{ color: "var(--text)" }}>{rForm.available}</b></div>
        </div>
        <label className="label-caps block mb-1">Quantidade a devolver</label>
        <input className="input mono" type="number" min="1" max={rForm.available} value={rForm.quantity} onChange={(e) => setRForm({ ...rForm, quantity: e.target.value })} data-testid="return-qty" />
      </Modal>
    </div>
  );
}
